import classNames from 'classnames';
import React from 'react';
import { useLocation } from 'react-router';

import { StylesheetProvider } from '../../contexts/StylesheetProvider';
import { TicketProvider } from '../../contexts/TicketProvider';
import { FCWithChild } from '../../interfaces/Shared';
import { MultiLingualStylesheet } from '../../interfaces/Stylesheet';
import { Ticket } from '../../interfaces/Ticket';
import StepsService from '../../services/StepsService';
import { ReactLifeCycleErrorBoundary } from '../Error/ReactLifecycleErrorBoundary';
import FadeInOutWrapper from '../FadeInOutWrapper/FadeInOutWrapper';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import { PageGuard } from '../PageGuard/PageGuard';
import { StepsProvider } from '../Steps/StepsProvider';

export type AppViewProps = {
  brandStylesheet: MultiLingualStylesheet | undefined;
  errorMode: boolean;
  globalStylesheet: MultiLingualStylesheet;
  ticket: Ticket | undefined;
};

export const AppView: FCWithChild<AppViewProps> = ({ ticket, globalStylesheet, brandStylesheet, errorMode, children }) => {
  const { pathname } = useLocation();
  const pagesWithFullScreenMinHeight = ['questions', 'contact'];
  const pagesWithWhiteBackground = ['customer'];

  const matchPathname = (toMatch: string) => {
    return new RegExp(`/[a-zA-Z0-9]+/${toMatch}`).test(pathname);
  };

  const pageClasses = classNames(
    'page',
    pagesWithWhiteBackground.some(matchPathname) && 'whiteBackground',
    !StepsService.showSteps(pathname) && 'pageWithoutStepper',
    pagesWithFullScreenMinHeight.some(matchPathname) && 'min-height-fullscreen'
  );

  // in error mode we don't need to Guard, just display the children without any changes
  const Guard = errorMode ? React.Fragment : PageGuard;

  return (
    <TicketProvider defaultTicket={ticket}>
      <StylesheetProvider brandStylesheet={brandStylesheet} globalStylesheet={globalStylesheet}>
        <StepsProvider>
          <Guard>
            <ReactLifeCycleErrorBoundary>
              <Header hideStepper={errorMode}/>
              <main className={pageClasses}>
                <FadeInOutWrapper isEnabled>
                  <div className="container">
                    <ReactLifeCycleErrorBoundary>
                      { children }
                    </ReactLifeCycleErrorBoundary>
                  </div>
                </FadeInOutWrapper>
              </main>
              <Footer hideHelpSection={errorMode}/>
            </ReactLifeCycleErrorBoundary>
          </Guard>
        </StepsProvider>
      </StylesheetProvider>
    </TicketProvider>
  );
};
